import { Cross1Icon, DownloadIcon, UploadIcon } from '@radix-ui/react-icons';
import { Box, Flex, IconButton, Text, Tooltip } from '@radix-ui/themes';
import React, { useRef, useState } from 'react';

interface DragAndDropFileUploadProps {
  onFileDrop: (file: File | null) => void;
  label?: string;
  accept?: string;
  selectedFile?: File | null;
  remove?: any
  isReadOnly?: boolean,
  disabled?: boolean
  required?: boolean;
}

/** Apresenta o tamanho do ficheiro numa unidade legível. */
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DragAndDropFileUpload: React.FC<DragAndDropFileUploadProps> = ({ onFileDrop, label = "Arraste e solte o ficheiro ou clique para selecionar", accept = "*", selectedFile, remove, isReadOnly = false, disabled = false }) => {
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    setDragging(true);
  };

  const getFileDownloadUrl = (file: File) => {
    return URL.createObjectURL(file);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (file) {
      onFileDrop(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileDrop(file);
    }
  };
  const handleClick = () => {
    if (disabled) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onFileDrop(null);
    if (remove) {
      remove();
    }
  };
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={label}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      style={{
        padding: 'var(--space-5)',
        border: `2px dashed ${dragging ? 'var(--accent-9)' : 'var(--gray-6)'}`,
        backgroundColor: dragging ? 'var(--accent-2)' : 'var(--color-surface)',
        borderRadius: 'var(--radius-3)',
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
        color: 'var(--gray-11)',
      }}
    >
      <input
        ref={fileInputRef}
        disabled={disabled}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {selectedFile ? (
        <Flex direction="row" gap="3" align="center" justify="center" wrap="wrap">
          <Text size="2" weight="medium">{selectedFile.name}</Text>
          <Text size="1" color="gray">{formatFileSize(selectedFile.size)}</Text>
          <Tooltip content="Descarregar">
            <IconButton color="green" size="1" aria-label={`Descarregar ${selectedFile.name}`} onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              const downloadUrl = getFileDownloadUrl(selectedFile);
              const a = document.createElement('a');
              a.href = downloadUrl;
              a.download = selectedFile.name;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(downloadUrl);
            }}>
              <DownloadIcon width="14" height="14" />
            </IconButton>
          </Tooltip>
          {
            isReadOnly ? <></> :
              <Tooltip content="Remover">
                <IconButton color="red" size="1" aria-label={`Remover ${selectedFile.name}`} onClick={handleRemoveFile} >
                  <Cross1Icon width="14" height="14" />
                </IconButton>
              </Tooltip>
          }
        </Flex>
      ) : (
        <Flex direction="column" align="center" gap="2">
          <UploadIcon width="20" height="20" />
          <Text size="2">
            {dragging ? 'Solte o ficheiro aqui…' : label}
          </Text>
        </Flex>
      )}
    </Box>
  );
};
